import { a as atBreakpointChange } from './at-breakpoint-change-929474bb.js';
import { E as EVENTS } from './events-55e4fd80.js';
import { g as getMediaQuery } from './media-queries-5a9283a8.js';
import './srraf.es-487187f3.js';

const { SWIPE_NAV_PREV: SWIPE_NAV_PREV$1, SWIPE_NAV_NEXT: SWIPE_NAV_NEXT$1 } = EVENTS;

class SwipeNav {
  constructor(source) {
    this.source = source;
    this.prevEvent = new Event(SWIPE_NAV_PREV$1);
    this.nextEvent = new Event(SWIPE_NAV_NEXT$1);
  }

  init(swipeableElement) {
    this.controller = new AbortController();
    this.eventOptions = { signal: this.controller.signal };

    swipeableElement.addEventListener(
      "pointerdown",
      this._handleSwipeStart.bind(this),
      this.eventOptions,
    );
    swipeableElement.addEventListener(
      "pointerup",
      this._handleSwipeEnd.bind(this),
      this.eventOptions,
    );
  }

  _handleSwipeStart(event) {
    this.swipeStartX = event.offsetX;
    this.swipeStartY = event.offsetY;
  }

  _handleSwipeEnd(event) {
    this.swipeEndX = event.offsetX;
    this.swipeEndY = event.offsetY;

    // For a swipe to register, it can only deviate on the y-axis by 20px, and must be 15px, or more, on the x-axis
    if (
      Math.abs(this.swipeStartY - this.swipeEndY) <= 20 &&
      Math.abs(this.swipeStartX - this.swipeEndX) >= 15
    ) {
      if (this.swipeStartX > this.swipeEndX) {
        this.source.dispatchEvent(this.nextEvent);
      } else if (this.swipeStartX < this.swipeEndX) {
        this.source.dispatchEvent(this.prevEvent);
      }
    }
  }

  destroy() {
    this.controller?.abort();
  }
}

const {
  SLIDESHOW_PAGINATION_CONNECTED,
  SLIDESHOW_PAGINATION_BULLET_CLICKED,
  SLIDESHOW_PAGINATION_SET_ACTIVE_BULLET,
  SLIDESHOW_PAGINATION_START_AUTOPLAY,
  SLIDESHOW_PAGINATION_PAUSE_AUTOPLAY,
  SLIDESHOW_PAGINATION_RESUME_AUTOPLAY,
  SLIDESHOW_PAGINATION_AUTOPLAY_ADVANCE,
  SLIDESHOW_PAGINATION_NAV_CLICK,
  SWIPE_NAV_PREV,
  SWIPE_NAV_NEXT,
  SHOPIFY_BLOCK_SELECT,
  SHOPIFY_BLOCK_DESELECT,
  WINDOW_WIDTH_CHANGED,
} = EVENTS;

class QuotesSection extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      quoteSlider: "[js-quote-slider]",
      quoteSliderPrevButton: "[js-quote-slider-prev-button]",
      quoteSliderNextButton: "[js-quote-slider-next-button]",
      quotesContent: "[js-quotes-content]",
      quote: "[js-quote]",
      quoteByBlockId: (id) => `[js-quote][data-block-id="${id}"]`,
      parentSection: ".shopify-section",
      sectionInner: "[js-section-inner]",
    };

    this.classes = {
      currentPrev: "quote--current-prev",
      currentNext: "quote--current-next",
      quoteTranstionOutPrev: "quote--transition-out-prev",
      quoteTranstionOutNext: "quote--transition-out-next",
      visible: "became-visible",
    };

    this.breakpoints = {
      tablet: getMediaQuery("tablet"),
    };
  }

  connectedCallback() {
    this.sectionId = this.getAttribute("data-section-id");
    this.quoteSlider = this.querySelector(this.selectors.quoteSlider);
    this.quoteSliderPrevButton = this.querySelector(
      this.selectors.quoteSliderPrevButton,
    );
    this.quoteSliderNextButton = this.querySelector(
      this.selectors.quoteSliderNextButton,
    );
    this.quotesContent = this.querySelector(this.selectors.quotesContent);
    this.quotes = this.querySelectorAll(this.selectors.quote);
    this.quoteList = Array.from(this.quotes);
    this.currentQuote = this.quotes[0];
    this.currentQuoteIndex = "0";
    this.firstQuoteIndex = "0";
    this.lastQuoteIndex = `${this.quotes.length - 1}`;
    this.swipeNav = new SwipeNav(this);
    this.sectionInner = this.querySelector(this.selectors.sectionInner);
    this.controller = new AbortController();
    this.eventOptions = { signal: this.controller.signal };
    this.blockSelected = false;

    // When sections are re-ordered in the theme editor, any custom elements
    // inside the section are disconnected and reconnected as the section is moved, and
    // that can potentially happen multiple times depending on how far the section is
    // moved. Because of that, and how this custom element interacts with the
    // "Slideshow pagination" element, the autoplay advance event listener can potentially
    // be added multiple times. As such, we need to save the callback function to a variable
    // so that the same function is used each time, which will prevent duplicate listeners.
    this.boundGoToNextQuote = this.goToNextQuote.bind(this);

    if (this.quotes.length > 1) {
      // Watch for window resize to update content width to account for nav button placement
      //
      // If there's enough space in the gutters for the buttons, then the content width can
      // be 100%. Otherwise, we need to adjust the content width.
      document.addEventListener(
        WINDOW_WIDTH_CHANGED,
        this.contentHandleWindowResize.bind(this),
        this.eventOptions,
      );
      this.contentHandleWindowResize();

      // Pagination
      window.addEventListener(
        SLIDESHOW_PAGINATION_BULLET_CLICKED(this.sectionId),
        this.handlePaginationBulletClick.bind(this),
        this.eventOptions,
      );

      window.addEventListener(
        SLIDESHOW_PAGINATION_NAV_CLICK(this.sectionId),
        this.handlePaginationNavClick.bind(this),
        this.eventOptions,
      );

      if (this.getAttribute("data-enable-autoplay") === "true") {
        const startAutoplayEvent = new Event(
          SLIDESHOW_PAGINATION_START_AUTOPLAY(this.sectionId),
        );

        // Wait until section has become visible before starting autoplay
        const quotesContentObserver = this.createClassMutationObserver(() => {
          this.initAutoplay();

          // We don't want autoplay to run if a block is selected
          if (!this.blockSelected) {
            window.dispatchEvent(startAutoplayEvent);
          }
        });

        window.addEventListener(
          SLIDESHOW_PAGINATION_CONNECTED(this.sectionId),
          () => {
            quotesContentObserver.observe(this.quotesContent, {
              attributeFilter: ["class"],
            });
          },
          this.eventOptions,
        );
      }

      // Navigation buttons
      this.quoteSliderPrevButton.addEventListener(
        "click",
        this.goToPreviousQuote.bind(this),
        this.eventOptions,
      );
      this.quoteSliderNextButton.addEventListener(
        "click",
        this.goToNextQuote.bind(this),
        this.eventOptions,
      );

      // Swipe navigation
      this.addEventListener(
        SWIPE_NAV_PREV,
        this.goToPreviousQuote.bind(this),
        this.eventOptions,
      );
      this.addEventListener(
        SWIPE_NAV_NEXT,
        this.goToNextQuote.bind(this),
        this.eventOptions,
      );

      // Watch for tablet breakpoint to handle swipe and navigation button functionality
      atBreakpointChange(720, this.swipeNavHandleWindowResize.bind(this));
      this.swipeNavHandleWindowResize();

      // Theme editor functionality
      if (window.Shopify.designMode) {
        this.parentSection = this.closest(this.selectors.parentSection);

        // Block events
        this.parentSection.addEventListener(
          SHOPIFY_BLOCK_SELECT,
          this.handleQuoteSelect.bind(this),
          this.eventOptions,
        );
        this.parentSection.addEventListener(
          SHOPIFY_BLOCK_DESELECT,
          this.handleQuoteDeselect.bind(this),
          this.eventOptions,
        );

        // When sections are re-ordered in the theme editor, the HTML is preserved, so we need to make sure
        // that the "current" quote is accurate.
        Array.from(this.quotes).some((quote) => {
          if (
            quote.classList.contains(this.classes.currentPrev) ||
            quote.classList.contains(this.classes.currentNext)
          ) {
            this.currentQuote = quote;
            this.currentQuoteIndex = quote.getAttribute("data-quote-index");
            return true
          } else {
            return false
          }
        });
      }
    }
  }

  // This sets a CSS variable for the available "gutter" space. The CSS then uses this to calculate
  // whether the content width needs to be adjusted.
  contentHandleWindowResize() {
    const outerGap = window.innerWidth - this.sectionInner.offsetWidth;
    this.quotesContent.style.setProperty("--outer-gap", `${outerGap}px`);
  }

  handlePaginationBulletClick(event) {
    this.changeQuote(event.detail.index);
  }

  handlePaginationNavClick(event) {
    this.changeQuote(this.getNewQuoteIndex(event.detail.direction), true);
  }

  initAutoplay() {
    // We use 'this.boundGoToNextQuote' so that the event listener callback is always the same
    // function. Refer to the instantiation of 'this.boundGoToNextQuote' for more information.
    window.addEventListener(
      SLIDESHOW_PAGINATION_AUTOPLAY_ADVANCE(this.sectionId),
      this.boundGoToNextQuote,
      this.eventOptions,
    );
  }

  swipeNavHandleWindowResize() {
    if (window.matchMedia(this.breakpoints.tablet).matches) {
      this.swipeNav?.destroy();
    } else {
      this.swipeNav.init(this.quoteSlider);
    }
  }

  changeQuote(index, fromNavButton = false) {
    // If the next quote is the same as the current, don't change
    if (index === this.currentQuoteIndex) return

    // Remove transition class
    this.quotes.forEach((quote) => {
      quote.classList.remove(
        this.classes.quoteTranstionOutPrev,
        this.classes.quoteTranstionOutNext,
      );
    });

    // Get current and new quote elements
    const currentQuote = this.currentQuote;
    const newQuote = this.quoteList.find((quote) => {
      return quote.getAttribute("data-quote-index") === index
    });
    const currentQuoteIndexNumber = parseInt(this.currentQuoteIndex, 10);
    const indexNumber = parseInt(index, 10);

    let currentQuoteClass, newQuoteClass;

    if (indexNumber > currentQuoteIndexNumber) {
      if (
        this.currentQuoteIndex === "0" &&
        index === this.lastQuoteIndex &&
        fromNavButton
      ) {
        currentQuoteClass = this.classes.quoteTranstionOutPrev;
        newQuoteClass = this.classes.currentPrev;
      } else {
        currentQuoteClass = this.classes.quoteTranstionOutNext;
        newQuoteClass = this.classes.currentNext;
      }
    } else if (indexNumber < currentQuoteIndexNumber) {
      if (
        this.currentQuoteIndex === this.lastQuoteIndex &&
        index === "0" &&
        fromNavButton
      ) {
        currentQuoteClass = this.classes.quoteTranstionOutNext;
        newQuoteClass = this.classes.currentNext;
      } else {
        currentQuoteClass = this.classes.quoteTranstionOutPrev;
        newQuoteClass = this.classes.currentPrev;
      }
    }

    // Update classes and attributes to transition quotes
    currentQuote.classList.add(currentQuoteClass);
    currentQuote.classList.remove(
      this.classes.currentPrev,
      this.classes.currentNext,
    );
    newQuote.classList.add(newQuoteClass);
    currentQuote.setAttribute("inert", "");
    this.currentQuote = newQuote;
    this.currentQuoteIndex = index;
    this.currentQuote.removeAttribute("inert");

    // Emit event to update pagination display
    const changeActivePaginationBulletEvent = new CustomEvent(
      SLIDESHOW_PAGINATION_SET_ACTIVE_BULLET(this.sectionId),
      { detail: { index } },
    );

    window.dispatchEvent(changeActivePaginationBulletEvent);
  }

  getNewQuoteIndex(direction) {
    let newQuoteIndex;

    if (direction === "prev") {
      if (this.currentQuote.getAttribute("data-quote-index") === "0") {
        newQuoteIndex = this.lastQuoteIndex;
      } else {
        newQuoteIndex = `${parseInt(this.currentQuoteIndex, 10) - 1}`;
      }
    } else {
      if (
        this.currentQuote.getAttribute("data-quote-index") ===
        this.lastQuoteIndex
      ) {
        newQuoteIndex = "0";
      } else {
        newQuoteIndex = `${parseInt(this.currentQuoteIndex, 10) + 1}`;
      }
    }

    return newQuoteIndex
  }

  goToPreviousQuote() {
    this.changeQuote(this.getNewQuoteIndex("prev"), true);
  }

  goToNextQuote(event) {
    const fromNavButton = event.type === ("click" );

    this.changeQuote(this.getNewQuoteIndex("next"), fromNavButton);
  }

  createClassMutationObserver(callback) {
    return new MutationObserver((mutations, observer) => {
      for (const mutation of mutations) {
        if (mutation.target.classList.contains(this.classes.visible)) {
          callback();
          observer.disconnect();
        }
      }
    })
  }

  handleQuoteSelect(event) {
    this.blockSelected = true;

    const selectedQuote = this.querySelector(
      this.selectors.quoteByBlockId(event.detail.blockId),
    );
    const pauseAutoplayEvent = new Event(
      SLIDESHOW_PAGINATION_PAUSE_AUTOPLAY(this.sectionId),
    );

    if (
      this.quotes.length > 1 &&
      this.getAttribute("data-enable-autoplay") === "true"
    ) {
      window.dispatchEvent(pauseAutoplayEvent);
    }

    if (this.currentQuote !== selectedQuote) {
      this.changeQuote(selectedQuote.getAttribute("data-quote-index"));
    }
  }

  handleQuoteDeselect() {
    this.blockSelected = false;

    if (
      this.quotes.length > 1 &&
      this.getAttribute("data-enable-autoplay") === "true"
    ) {
      const resumeAutoplayEvent = new Event(
        SLIDESHOW_PAGINATION_RESUME_AUTOPLAY(this.sectionId),
      );
      window.dispatchEvent(resumeAutoplayEvent);
    }
  }

  disconnectedCallback() {
    this.controller.abort();
    this.swipeNav?.destroy();
  }
}

if (!customElements.get("quotes-section")) {
  customElements.define("quotes-section", QuotesSection);
}
